const PLACEHOLDER_NAME_RE =
  /^(some(\s|$)|test(\s|$)|placeholder|untitled|my family|family name|some name|some family)/i;

type NullableString = string | null | undefined;

export function looksLikePlaceholderName(value: NullableString): boolean {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return true;
  if (trimmed.length < 2) return true;
  return PLACEHOLDER_NAME_RE.test(trimmed);
}

export function placeholderNameFieldError(value: string, fieldLabel: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return `${fieldLabel} is required`;
  }
  if (looksLikePlaceholderName(trimmed)) {
    return `Enter a real ${fieldLabel.toLowerCase()} (avoid placeholders like "some family" or "test")`;
  }
  return undefined;
}

/** Mask PAN for display: ABCDE1234F → ABC**234F */
export function maskPanNumber(panNumber: string): string {
  if (panNumber?.length === 10) {
    return `${panNumber.substring(0, 3)}**${panNumber.substring(5)}`;
  }
  return panNumber;
}

export interface FamilySetupChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

/**
 * Build Family Setup Checklist Items.
 */
export function buildFamilySetupChecklistItems(input: {
  familyName: string;
  memberCount: number;
  goalsCount: number;
}): FamilySetupChecklistItem[] {
  return [
    {
      id: "family-name",
      label: "Give your family a real name",
      done: !looksLikePlaceholderName(input.familyName),
    },
    {
      id: "members",
      label: "Add at least one more family member",
      done: input.memberCount >= 2,
    },
    {
      id: "goals",
      label: "Create your first family goal",
      done: input.goalsCount > 0,
    },
  ];
}

export function shouldShowFamilySetupChecklist(items: FamilySetupChecklistItem[]): boolean {
  return items.some((item) => !item.done);
}

/**
 * Get Member Verification Status.
 */
export function getMemberVerificationStatus(input: {
  memberUserId: string | null | undefined;
  currentUserId: string | undefined;
  emailConfirmed: boolean;
  currentUserPhone: string | null | undefined;
}) {
  const isCurrentUser = input.memberUserId === input.currentUserId;
  const hasVerifiedEmail = isCurrentUser ? input.emailConfirmed : false;

  return {
    email: hasVerifiedEmail,
    phone: isCurrentUser ? Boolean(input.currentUserPhone) : false,
    overall: hasVerifiedEmail,
    isCurrentUser,
  };
}

export interface MemberProfileCompletenessInput {
  full_name: string;
  relationship: string;
  role: string;
  date_of_birth: string | null;
  pan_number: string;
  user_id?: string | null;
}

/**
 * Compute Family Profile Completeness Average.
 */
export function computeFamilyProfileCompletenessAverage(
  members: MemberProfileCompletenessInput[],
  contextByUserId?: Map<
    string,
    Pick<MemberProfileCompletenessContext, "hasVerifiedEmail" | "hasPhoneOnFile">
  >
): number {
  if (!members.length) return 0;
  const total = members.reduce((sum, member) => {
    const memberContext = contextByUserId?.get(member.user_id ?? "") ?? {
      hasVerifiedEmail: false,
      hasPhoneOnFile: false,
    };
    const completeness = computeMemberProfileCompleteness(member, {
      hasLinkedAccount: Boolean(member.user_id),
      hasVerifiedEmail: memberContext.hasVerifiedEmail,
      hasPhoneOnFile: memberContext.hasPhoneOnFile,
    });
    return sum + completeness.percentage;
  }, 0);
  return Math.round(total / members.length);
}

export interface MemberProfileCompletenessContext {
  hasLinkedAccount: boolean;
  hasVerifiedEmail: boolean;
  hasPhoneOnFile: boolean;
}

export interface MemberProfileCompleteness {
  percentage: number;
  missingFields: string[];
}

/**
 * Compute Member Profile Completeness.
 */
export function computeMemberProfileCompleteness(
  member: MemberProfileCompletenessInput,
  context: MemberProfileCompletenessContext
): MemberProfileCompleteness {
  const checks = [
    { label: "Full name", done: !looksLikePlaceholderName(member.full_name) },
    { label: "Relationship", done: Boolean(member.relationship?.trim()) },
    { label: "Role", done: Boolean(member.role?.trim()) },
    { label: "Date of birth", done: Boolean(member.date_of_birth) },
    { label: "PAN", done: Boolean(member.pan_number?.trim()) },
    { label: "Linked account", done: context.hasLinkedAccount },
    { label: "Verified email", done: context.hasVerifiedEmail },
    { label: "Phone", done: context.hasPhoneOnFile },
  ];

  const doneCount = checks.filter((check) => check.done).length;
  const percentage = Math.round((doneCount / checks.length) * 100);

  return {
    percentage,
    missingFields: checks.filter((check) => !check.done).map((check) => check.label),
  };
}
